import React, { useState, useEffect } from 'react';
import { Plus, Package, Edit2, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Download, Printer, Copy } from 'lucide-react';
import { ProductModal } from './ProductModal';
import { deleteProduct, updateProduct, batchReorderProducts, duplicateProduct } from '../hooks/useStoreApi';
import { AdminTabEmpty } from './store/admin/AdminTabEmpty';
import { useVocab, useIsAppointments } from '../hooks/useVocab';

// Products Tab Component (extracted from StoreAdmin.jsx)
// Props:
// - storeId: numeric store identifier
// - products: array of product objects
// - categories: array of category objects
// - refetch: function to refetch products
// - onRegisterRefresh: callback(tabId, handler) to register refresh logic
export const ProductsTab = ({ storeId, store, products, categories, refetch, onRegisterRefresh, chatId, intent, onIntentConsumed }) => {
    const t = useVocab();
    const isAppt = useIsAppointments();
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    // Category filter state ("all" means no filter)
    const [categoryFilter, setCategoryFilter] = useState('all');
    // Track pending reorder changes: { productId: { direction, categoryId } }
    const [pendingReorders, setPendingReorders] = useState([]);
    const [isSavingReorders, setIsSavingReorders] = useState(false);
    // Track which product is currently being duplicated (product_id or null)
    const [duplicatingId, setDuplicatingId] = useState(null);

    // External create-intent (AI chat / command palette): open the new-product modal.
    useEffect(() => {
        if (intent === 'create') {
            setEditingProduct(null);
            setShowModal(true);
            onIntentConsumed?.();
        }
    }, [intent, onIntentConsumed]);

    useEffect(() => {
        if (onRegisterRefresh) {
            onRegisterRefresh('products', async () => {
                await refetch?.();
            });
        }
    }, [onRegisterRefresh, refetch]);

    const handleDelete = async (productId) => {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק את המוצר?')) return;

        try {
            await deleteProduct(storeId, productId, chatId);
            refetch();
        } catch (err) {
            alert('שגיאה במחיקת המוצר: ' + err.message);
        }
    };

    const handleDuplicate = async (product) => {
        if (!window.confirm(`האם לשכפל את המוצר "${product.name}"? ייווצר מוצר חדש עם אותו מידע ותמונה.`)) return;

        setDuplicatingId(product.product_id);
        try {
            await duplicateProduct(storeId, product.product_id, chatId);
            await refetch();
        } catch (err) {
            alert('שגיאה בשכפול המוצר: ' + err.message);
        } finally {
            setDuplicatingId(null);
        }
    };

    const handleToggleAvailability = async (product) => {
        const isAvailable = product.available !== false; // Default to true if undefined
        const newAvailability = !isAvailable;

        try {
            await updateProduct(storeId, product.product_id, { available: newAvailability }, chatId);
            refetch();
        } catch (err) {
            alert('שגיאה בעדכון זמינות המוצר: ' + err.message);
        }
    };

    const handleReorder = (productId, direction, productCategoryId) => {
        // Track this reorder change locally without calling the API
        setPendingReorders(prev => [...prev, { productId, direction, categoryId: productCategoryId }]);
    };

    const handleSaveReorders = async () => {
        if (pendingReorders.length === 0) return;

        setIsSavingReorders(true);
        try {
            // Execute all pending reorders in one batch API call
            await batchReorderProducts(storeId, pendingReorders, chatId);
            // Clear pending reorders and refetch
            setPendingReorders([]);
            await refetch();
        } catch (err) {
            alert('שגיאה בשמירת שינויי הסדר: ' + err.message);
        } finally {
            setIsSavingReorders(false);
        }
    };

    const handleCancelReorders = () => {
        setPendingReorders([]);
    };

    // Get only products that are visible in the store (available = true)
    const getVisibleStoreProducts = () => {
        return visibleProducts.filter(p => p.available !== false);
    };

    const handlePrintAsText = () => {
        const visibleStoreProducts = getVisibleStoreProducts();

        if (visibleStoreProducts.length === 0) {
            alert('אין מוצרים גלויים להדפסה');
            return;
        }

        let content = '=== רשימת מוצרים ===\n\n';

        // Group products by category
        const productsByCategory = {};
        const uncategorized = [];

        visibleStoreProducts.forEach(product => {
            if (product.category_id) {
                if (!productsByCategory[product.category_id]) {
                    productsByCategory[product.category_id] = [];
                }
                productsByCategory[product.category_id].push(product);
            } else {
                uncategorized.push(product);
            }
        });

        let productNumber = 1;

        // Print categorized products
        categories?.forEach(category => {
            const categoryProducts = productsByCategory[category.category_id];
            if (categoryProducts && categoryProducts.length > 0) {
                content += `\n━━━ ${category.name} ━━━\n\n`;

                categoryProducts.forEach(product => {
                    const inv = product.inventory || {};

                    content += `${productNumber}. ${product.name}\n`;
                    content += `   תיאור: ${product.description || 'אין תיאור'}\n`;
                    content += `   מחיר: ₪${product.price}\n`;
                    content += `   מלאי: ${inv.enabled ? inv.quantity : 'ללא הגבלה'}\n`;

                    if (product.paid_options && product.paid_options.length > 0) {
                        content += `   תוספות בתשלום:\n`;
                        product.paid_options.forEach(option => {
                            content += `      - ${option.name}: ₪${option.price}\n`;
                        });
                    }
                    content += '\n';
                    productNumber++;
                });
            }
        });

        // Print uncategorized products
        if (uncategorized.length > 0) {
            content += `\n━━━ ללא קטגוריה ━━━\n\n`;

            uncategorized.forEach(product => {
                const inv = product.inventory || {};

                content += `${productNumber}. ${product.name}\n`;
                content += `   תיאור: ${product.description || 'אין תיאור'}\n`;
                content += `   מחיר: ₪${product.price}\n`;
                content += `   מלאי: ${inv.enabled ? inv.quantity : 'ללא הגבלה'}\n`;

                if (product.paid_options && product.paid_options.length > 0) {
                    content += `   תוספות בתשלום:\n`;
                    product.paid_options.forEach(option => {
                        content += `      - ${option.name}: ₪${option.price}\n`;
                    });
                }
                content += '\n';
                productNumber++;
            });
        }

        content += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        content += `סה"כ מוצרים גלויים: ${visibleStoreProducts.length}\n`;
        content += `תאריך: ${new Date().toLocaleDateString('he-IL')}\n`;

        // Download as text file
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `products_${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handlePrintAsPDF = () => {
        const visibleStoreProducts = getVisibleStoreProducts();

        if (visibleStoreProducts.length === 0) {
            alert('אין מוצרים גלויים להדפסה');
            return;
        }

        // Group products by category
        const productsByCategory = {};
        const uncategorized = [];

        visibleStoreProducts.forEach(product => {
            if (product.category_id) {
                if (!productsByCategory[product.category_id]) {
                    productsByCategory[product.category_id] = [];
                }
                productsByCategory[product.category_id].push(product);
            } else {
                uncategorized.push(product);
            }
        });

        // Create a print-friendly HTML document for PDF
        const printWindow = window.open('', '_blank');

        let content = `
            <!DOCTYPE html>
            <html dir="rtl" lang="he">
            <head>
                <meta charset="UTF-8">
                <title>רשימת מוצרים - PDF</title>
                <style>
                    @page {
                        size: A4;
                        margin: 2cm;
                    }
                    body {
                        font-family: 'Arial', 'Helvetica', sans-serif;
                        padding: 0;
                        margin: 0;
                        direction: rtl;
                        font-size: 12pt;
                    }
                    h1 {
                        text-align: center;
                        color: #333;
                        border-bottom: 3px solid #4A90E2;
                        padding-bottom: 10px;
                        margin-bottom: 10px;
                        font-size: 24pt;
                    }
                    .date {
                        text-align: center;
                        color: #666;
                        margin-bottom: 20px;
                        font-size: 11pt;
                    }
                    .category-header {
                        background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
                        color: white;
                        padding: 12px 15px;
                        margin-top: 25px;
                        margin-bottom: 15px;
                        border-radius: 8px;
                        font-weight: bold;
                        font-size: 16pt;
                        text-align: center;
                        page-break-after: avoid;
                    }
                    .product {
                        margin-bottom: 20px;
                        padding: 15px;
                        border: 2px solid #ddd;
                        border-radius: 10px;
                        page-break-inside: avoid;
                        background-color: #fafafa;
                    }
                    .product-name {
                        font-size: 15pt;
                        font-weight: bold;
                        color: #333;
                        margin-bottom: 8px;
                        border-bottom: 1px solid #e0e0e0;
                        padding-bottom: 5px;
                    }
                    .product-detail {
                        margin: 6px 0;
                        color: #555;
                        line-height: 1.5;
                    }
                    .product-detail strong {
                        color: #333;
                        font-weight: 600;
                    }
                    .product-price {
                        font-size: 14pt;
                        font-weight: bold;
                        color: #4A90E2;
                        margin: 8px 0;
                    }
                    .options {
                        margin-top: 10px;
                        padding: 10px;
                        background-color: #f0f0f0;
                        border-radius: 5px;
                        border-right: 3px solid #4A90E2;
                    }
                    .options-title {
                        font-weight: bold;
                        margin-bottom: 5px;
                        color: #333;
                    }
                    .option-item {
                        margin: 4px 0;
                        padding-right: 15px;
                        color: #555;
                    }
                    .footer {
                        margin-top: 30px;
                        padding-top: 15px;
                        border-top: 3px solid #4A90E2;
                        text-align: center;
                        font-weight: bold;
                        font-size: 13pt;
                        page-break-before: avoid;
                    }
                    @media print {
                        body {
                            padding: 0;
                        }
                        .product {
                            page-break-inside: avoid;
                        }
                        .category-header {
                            page-break-after: avoid;
                        }
                    }
                </style>
            </head>
            <body>
                <h1>רשימת מוצרים</h1>
                <div class="date">תאריך: ${new Date().toLocaleDateString('he-IL')}</div>
        `;

        let productNumber = 1;

        // Print categorized products
        categories?.forEach(category => {
            const categoryProducts = productsByCategory[category.category_id];
            if (categoryProducts && categoryProducts.length > 0) {
                content += `<div class="category-header">${category.name}</div>`;

                categoryProducts.forEach(product => {
                    const inv = product.inventory || {};

                    content += `
                        <div class="product">
                            <div class="product-name">${productNumber}. ${product.name}</div>
                            ${product.description ? `<div class="product-detail"><strong>תיאור:</strong> ${product.description}</div>` : ''}
                            <div class="product-detail product-price"><strong>מחיר:</strong> ₪${product.price}</div>
                            <div class="product-detail"><strong>מלאי:</strong> ${inv.enabled ? inv.quantity : 'ללא הגבלה'}</div>
                            ${product.paid_options && product.paid_options.length > 0 ? `
                                <div class="options">
                                    <div class="options-title">תוספות בתשלום:</div>
                                    ${product.paid_options.map(option =>
                                        `<div class="option-item">• ${option.name}: ₪${option.price}</div>`
                                    ).join('')}
                                </div>
                            ` : ''}
                        </div>
                    `;
                    productNumber++;
                });
            }
        });

        // Print uncategorized products
        if (uncategorized.length > 0) {
            content += `<div class="category-header">ללא קטגוריה</div>`;

            uncategorized.forEach(product => {
                const inv = product.inventory || {};

                content += `
                    <div class="product">
                        <div class="product-name">${productNumber}. ${product.name}</div>
                        ${product.description ? `<div class="product-detail"><strong>תיאור:</strong> ${product.description}</div>` : ''}
                        <div class="product-detail product-price"><strong>מחיר:</strong> ₪${product.price}</div>
                        <div class="product-detail"><strong>מלאי:</strong> ${inv.enabled ? inv.quantity : 'ללא הגבלה'}</div>
                        ${product.paid_options && product.paid_options.length > 0 ? `
                            <div class="options">
                                <div class="options-title">תוספות בתשלום:</div>
                                ${product.paid_options.map(option =>
                                    `<div class="option-item">• ${option.name}: ₪${option.price}</div>`
                                ).join('')}
                            </div>
                        ` : ''}
                    </div>
                `;
                productNumber++;
            });
        }

        content += `
                <div class="footer">
                    סה"כ מוצרים גלויים: ${visibleStoreProducts.length}
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(content);
        printWindow.document.close();

        // Wait for content to load, then trigger print dialog (user can save as PDF)
        printWindow.onload = () => {
            printWindow.print();
        };
    };

    const handlePrint = () => {
        const visibleStoreProducts = getVisibleStoreProducts();

        if (visibleStoreProducts.length === 0) {
            alert('אין מוצרים גלויים להדפסה');
            return;
        }

        // Group products by category
        const productsByCategory = {};
        const uncategorized = [];

        visibleStoreProducts.forEach(product => {
            if (product.category_id) {
                if (!productsByCategory[product.category_id]) {
                    productsByCategory[product.category_id] = [];
                }
                productsByCategory[product.category_id].push(product);
            } else {
                uncategorized.push(product);
            }
        });

        // Create a print-friendly HTML document
        const printWindow = window.open('', '_blank');

        let content = `
            <!DOCTYPE html>
            <html dir="rtl" lang="he">
            <head>
                <meta charset="UTF-8">
                <title>רשימת מוצרים</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        padding: 20px;
                        direction: rtl;
                    }
                    h1 {
                        text-align: center;
                        color: #333;
                        border-bottom: 2px solid #4A90E2;
                        padding-bottom: 10px;
                    }
                    .date {
                        text-align: center;
                        color: #666;
                        margin-bottom: 30px;
                    }
                    .category-header {
                        background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
                        color: white;
                        padding: 10px 15px;
                        margin-top: 25px;
                        margin-bottom: 15px;
                        border-radius: 8px;
                        font-weight: bold;
                        font-size: 18px;
                        text-align: center;
                        page-break-after: avoid;
                    }
                    .product {
                        margin-bottom: 25px;
                        padding: 15px;
                        border: 1px solid #ddd;
                        border-radius: 8px;
                        page-break-inside: avoid;
                    }
                    .product-name {
                        font-size: 18px;
                        font-weight: bold;
                        color: #333;
                        margin-bottom: 8px;
                        border-bottom: 1px solid #e0e0e0;
                        padding-bottom: 5px;
                    }
                    .product-detail {
                        margin: 5px 0;
                        color: #555;
                    }
                    .product-detail strong {
                        color: #333;
                    }
                    .product-price {
                        font-size: 16px;
                        font-weight: bold;
                        color: #4A90E2;
                    }
                    .options {
                        margin-top: 10px;
                        padding: 10px;
                        background-color: #f5f5f5;
                        border-radius: 5px;
                        border-right: 3px solid #4A90E2;
                    }
                    .options-title {
                        font-weight: bold;
                        margin-bottom: 5px;
                    }
                    .option-item {
                        margin: 3px 0;
                        padding-right: 15px;
                    }
                    .footer {
                        margin-top: 30px;
                        padding-top: 15px;
                        border-top: 2px solid #4A90E2;
                        text-align: center;
                        font-weight: bold;
                    }
                    @media print {
                        body { padding: 10px; }
                        .product { page-break-inside: avoid; }
                        .category-header { page-break-after: avoid; }
                    }
                </style>
            </head>
            <body>
                <h1>רשימת מוצרים</h1>
                <div class="date">תאריך: ${new Date().toLocaleDateString('he-IL')}</div>
        `;

        let productNumber = 1;

        // Print categorized products
        categories?.forEach(category => {
            const categoryProducts = productsByCategory[category.category_id];
            if (categoryProducts && categoryProducts.length > 0) {
                content += `<div class="category-header">${category.name}</div>`;

                categoryProducts.forEach(product => {
                    const inv = product.inventory || {};

                    content += `
                        <div class="product">
                            <div class="product-name">${productNumber}. ${product.name}</div>
                            ${product.description ? `<div class="product-detail"><strong>תיאור:</strong> ${product.description}</div>` : ''}
                            <div class="product-detail product-price"><strong>מחיר:</strong> ₪${product.price}</div>
                            <div class="product-detail"><strong>מלאי:</strong> ${inv.enabled ? inv.quantity : 'ללא הגבלה'}</div>
                            ${product.paid_options && product.paid_options.length > 0 ? `
                                <div class="options">
                                    <div class="options-title">תוספות בתשלום:</div>
                                    ${product.paid_options.map(option =>
                                        `<div class="option-item">• ${option.name}: ₪${option.price}</div>`
                                    ).join('')}
                                </div>
                            ` : ''}
                        </div>
                    `;
                    productNumber++;
                });
            }
        });

        // Print uncategorized products
        if (uncategorized.length > 0) {
            content += `<div class="category-header">ללא קטגוריה</div>`;

            uncategorized.forEach(product => {
                const inv = product.inventory || {};

                content += `
                    <div class="product">
                        <div class="product-name">${productNumber}. ${product.name}</div>
                        ${product.description ? `<div class="product-detail"><strong>תיאור:</strong> ${product.description}</div>` : ''}
                        <div class="product-detail product-price"><strong>מחיר:</strong> ₪${product.price}</div>
                        <div class="product-detail"><strong>מלאי:</strong> ${inv.enabled ? inv.quantity : 'ללא הגבלה'}</div>
                        ${product.paid_options && product.paid_options.length > 0 ? `
                            <div class="options">
                                <div class="options-title">תוספות בתשלום:</div>
                                ${product.paid_options.map(option =>
                                    `<div class="option-item">• ${option.name}: ₪${option.price}</div>`
                                ).join('')}
                            </div>
                        ` : ''}
                    </div>
                `;
                productNumber++;
            });
        }

        content += `
                <div class="footer">
                    סה"כ מוצרים גלויים: ${visibleStoreProducts.length}
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(content);
        printWindow.document.close();

        // Wait for content to load, then print
        printWindow.onload = () => {
            printWindow.print();
        };
    };

    // Apply pending reorders to get the locally reordered product list
    const getReorderedProducts = (productsList) => {
        if (!productsList || pendingReorders.length === 0) return productsList || [];

        // Group products by category to apply reorders within each category
        const categorizedProducts = {};
        productsList.forEach(product => {
            const catId = product.category_id || 'uncategorized';
            if (!categorizedProducts[catId]) {
                categorizedProducts[catId] = [];
            }
            categorizedProducts[catId].push(product);
        });

        // Apply all pending reorders to each category
        Object.keys(categorizedProducts).forEach(catId => {
            const categoryProducts = categorizedProducts[catId];

            // Apply each reorder for this category
            pendingReorders.forEach(reorder => {
                // Only apply reorders that match this category
                if (String(reorder.categoryId) !== String(catId) && catId !== 'uncategorized') return;

                const currentIndex = categoryProducts.findIndex(p => p.product_id === reorder.productId);
                if (currentIndex === -1) return;

                const newIndex = reorder.direction === 'up' ? currentIndex - 1 : currentIndex + 1;

                // Check if move is valid
                if (newIndex >= 0 && newIndex < categoryProducts.length) {
                    // Swap the products
                    const temp = categoryProducts[currentIndex];
                    categoryProducts[currentIndex] = categoryProducts[newIndex];
                    categoryProducts[newIndex] = temp;
                }
            });
        });

        // Flatten back to a single array
        return Object.values(categorizedProducts).flat();
    };

    // Order products grouped by category, following the categories array order.
    // Within-category order (incl. pending reorders) is preserved; products whose
    // category is unknown come after the known categories, uncategorized last.
    const orderProductsByCategory = (list) => {
        if (!list || list.length === 0) return list || [];

        const buckets = new Map();
        (categories || []).forEach(cat => buckets.set(String(cat.category_id), []));
        const uncategorized = [];

        list.forEach(product => {
            if (product.category_id == null) {
                uncategorized.push(product);
                return;
            }
            const key = String(product.category_id);
            if (!buckets.has(key)) buckets.set(key, []);
            buckets.get(key).push(product);
        });

        return [...Array.from(buckets.values()).flat(), ...uncategorized];
    };

    // Derive visible products after applying category grouping, filter and pending reorders
    const reorderedProducts = getReorderedProducts(products);
    const groupedProducts = orderProductsByCategory(reorderedProducts);
    const visibleProducts = groupedProducts.filter(p => categoryFilter === 'all' || p.category_id === categoryFilter);

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-card p-8 border border-mekomit-100">
                <div className="flex flex-col gap-4 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold text-mekomit-900">{t.catalogManage}</h2>
                            <p className="text-mekomit-600 text-sm mt-1">{isAppt ? 'נהל את השירותים שאפשר לקבוע איתך' : 'נהל את מוצרי החנות שלך'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Print/Download Dropdown */}
                            {products && products.length > 0 && (
                                <div className="relative group">
                                    <button
                                        className="flex items-center gap-2 px-4 py-3 bg-white border-2 border-mekomit-400 text-mekomit-600 rounded-xl hover:bg-mekomit-50 transition-all shadow-sm hover:shadow-md font-semibold whitespace-nowrap"
                                    >
                                        <Download className="w-5 h-5" />
                                        <span className="hidden sm:inline">הדפס/הורד</span>
                                    </button>
                                    {/* Dropdown menu */}
                                    <div className="absolute left-0 mt-2 w-48 bg-white border border-mekomit-200 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                        <button
                                            onClick={handlePrint}
                                            className="w-full flex items-center gap-3 px-4 py-3 text-right hover:bg-mekomit-50 transition-colors border-b border-mekomit-100"
                                        >
                                            <Printer className="w-4 h-4 text-mekomit-600" />
                                            <span className="text-sm font-medium text-mekomit-800">הדפס</span>
                                        </button>
                                        <button
                                            onClick={handlePrintAsPDF}
                                            className="w-full flex items-center gap-3 px-4 py-3 text-right hover:bg-mekomit-50 transition-colors border-b border-mekomit-100"
                                        >
                                            <Download className="w-4 h-4 text-mekomit-600" />
                                            <span className="text-sm font-medium text-mekomit-800">שמור כ-PDF</span>
                                        </button>
                                        <button
                                            onClick={handlePrintAsText}
                                            className="w-full flex items-center gap-3 px-4 py-3 text-right hover:bg-mekomit-50 transition-colors rounded-b-xl"
                                        >
                                            <Download className="w-4 h-4 text-mekomit-600" />
                                            <span className="text-sm font-medium text-mekomit-800">הורד טקסט</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                            <button
                                onClick={() => { setEditingProduct(null); setShowModal(true); }}
                                className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-white rounded-xl hover:from-mekomit-600 hover:to-mekomit-500 transition-all shadow-md hover:shadow-lg font-semibold whitespace-nowrap"
                            >
                                <Plus className="w-5 h-5" />
                                <span className="hidden sm:inline">{t.addItem}</span>
                            </button>
                        </div>
                    </div>
                    {pendingReorders.length > 0 && (
                        <div className="flex items-center justify-end gap-2 p-3 bg-warning-50 border border-warning-100 rounded-xl">
                            <button
                                onClick={handleCancelReorders}
                                disabled={isSavingReorders}
                                className="flex-1 sm:flex-none px-4 py-2 bg-white text-ink rounded-lg hover:bg-surface-muted transition-all shadow-sm font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed border border-line"
                            >
                                ביטול
                            </button>
                            <button
                                onClick={handleSaveReorders}
                                disabled={isSavingReorders}
                                className="flex-1 sm:flex-none px-5 py-2 bg-gradient-to-r from-success-500 to-success-500 text-white rounded-lg hover:from-success-600 hover:to-success-500 transition-all shadow-md hover:shadow-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSavingReorders ? 'שומר...' : 'שמור שינויים'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Category filter controls (shown only when products exist) */}
                {products && products.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3 mb-6 bg-mekomit-50/60 border border-mekomit-100 rounded-xl px-4 py-3">
                        <div className="flex items-center gap-2">
                            <label htmlFor="product-category-filter" className="text-sm font-medium text-mekomit-800">סינון לפי קטגוריה:</label>
                            <select
                                id="product-category-filter"
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                className="px-3 py-2 text-sm rounded-lg border border-mekomit-200 focus:ring-2 focus:ring-mekomit-400 bg-white min-w-[160px]"
                            >
                                <option value="all">כל הקטגוריות</option>
                                {categories?.map(cat => (
                                    <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                                ))}
                            </select>
                            {categoryFilter !== 'all' && (
                                <button
                                    type="button"
                                    onClick={() => setCategoryFilter('all')}
                                    className="text-xs px-2 py-1 rounded-md bg-surface-sunken hover:bg-line text-ink"
                                >
                                    נקה
                                </button>
                            )}
                        </div>
                        <div className="text-xs text-ink-muted flex-1">
                            <div>{`מספר ${t.items} מוצגים: ${visibleProducts.length} / ${products.length}`}</div>
                            <div className="text-[11px] text-mekomit-500 mt-0.5">
                                {`${t.items} גלויים: ${getVisibleStoreProducts().length}`}
                            </div>
                        </div>
                    </div>
                )}

                {!products || products.length === 0 ? (
                    <AdminTabEmpty
                        icon={Package}
                        tone="brand"
                        title={isAppt ? 'השירות הראשון — מכאן זה מתחיל' : 'המוצר הראשון — תמיד הכי מרגש'}
                        body={isAppt
                            ? 'שירות אחד מספיק כדי להתחיל לקבל תורים. תמיד אפשר לערוך, להוסיף תיאור ולשנות מחיר ומשך אחר כך. ככה המטופלים יוכלו לקבוע איתך ממש מהר.'
                            : 'מוצר אחד מספיק כדי להתחיל. תמיד אפשר לערוך, להוסיף תמונות ולשנות מחיר אחר כך. ככה הלקוחות יראו ממש מהר משהו אמיתי בחנות שלך.'}
                        examples={isAppt ? [
                            'שם ברור לשירות — למשל "פגישת ייעוץ ראשונית"',
                            'משך הפגישה והמחיר — כדי שהיומן יתמלא נכון',
                            'תיאור קצר שמסביר למי השירות מתאים',
                        ] : [
                            'תמונה ברורה במצב אור יום — זה השיווק הכי טוב',
                            'תיאור קצר עם 1-2 שורות שמסביר למה זה שווה',
                            'מחיר מדויק, ואפשר להוסיף מבצע מאוחר יותר',
                        ]}
                        primaryAction={{
                            label: isAppt ? 'להוסיף שירות ראשון' : 'להוסיף מוצר ראשון',
                            onClick: () => { setEditingProduct(null); setShowModal(true); },
                        }}
                    />
                ) : (
                    visibleProducts.length === 0 ? (
                        <div className="p-8 bg-mekomit-50 border border-mekomit-100 rounded-xl text-center text-sm text-mekomit-700">
                            {`אין ${t.items} בקטגוריה שנבחרה`}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {visibleProducts.map((product, index) => {
                                const productCategory = categories?.find(cat => cat.category_id === product.category_id);
                                const inv = product.inventory || {};
                                const soldOut = inv.enabled && inv.quantity === 0;
                                const isAvailable = product.available !== false; // Default to true if undefined
                                const isFirst = index === 0;
                                const isLast = index === visibleProducts.length - 1;
                                return (
                                    <div key={product.product_id} className={`group bg-white rounded-lg overflow-hidden border ${isAvailable ? 'border-mekomit-100 hover:border-mekomit-300' : 'border-line-strong opacity-75'} shadow-sm hover:shadow-md transition-all duration-300`}>
                                        {/* Product Image */}
                                        <div className="aspect-square bg-mekomit-50 relative overflow-hidden">
                                            {product.images && product.images.length > 0 ? (
                                                <img
                                                    src={product.images[0]}
                                                    alt={product.name}
                                                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${!isAvailable ? 'grayscale' : ''}`}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-mekomit-100">
                                                    <Package className="w-12 h-12 text-mekomit-300" />
                                                </div>
                                            )}
                                            {!isAvailable && (
                                                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                                                    <span className="text-white text-sm font-bold tracking-wide">מוסתר מהלקוחות</span>
                                                </div>
                                            )}
                                            {soldOut && isAvailable && (
                                                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                                                    <span className="text-white text-sm font-bold tracking-wide">אזל מהמלאי</span>
                                                </div>
                                            )}
                                            {/* Category Badge */}
                                            {productCategory && (
                                                <span className="absolute top-2 right-2 text-xs px-3 py-1 bg-mekomit-900/80 backdrop-blur text-white rounded-full font-medium">
                                                    {productCategory.name}
                                                </span>
                                            )}
                                            {/* Sale banner */}
                                            {product.sale?.enabled && product.sale?.percent > 0 && (
                                                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-r from-danger-600 to-danger-500 text-white text-center py-1 px-2 shadow-md">
                                                    <span className="text-xs font-bold tracking-wide">
                                                        במבצע · {product.sale.percent}% הנחה
                                                    </span>
                                                </div>
                                            )}
                                            {/* Reorder Buttons (only when viewing a specific category) */}
                                            {categoryFilter !== 'all' && (
                                                <div className="absolute top-2 left-2 flex flex-col gap-1">
                                                    <button
                                                        onClick={() => handleReorder(product.product_id, 'up', product.category_id)}
                                                        disabled={isFirst}
                                                        className={`p-1.5 rounded-md transition-all ${isFirst
                                                            ? 'bg-line/50 text-ink-subtle cursor-not-allowed'
                                                            : 'bg-white/90 hover:bg-white text-mekomit-600 hover:text-mekomit-700 shadow-sm hover:shadow-md'
                                                            }`}
                                                        title="הזז למעלה"
                                                    >
                                                        <ArrowUp className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleReorder(product.product_id, 'down', product.category_id)}
                                                        disabled={isLast}
                                                        className={`p-1.5 rounded-md transition-all ${isLast
                                                            ? 'bg-line/50 text-ink-subtle cursor-not-allowed'
                                                            : 'bg-white/90 hover:bg-white text-mekomit-600 hover:text-mekomit-700 shadow-sm hover:shadow-md'
                                                            }`}
                                                        title="הזז למטה"
                                                    >
                                                        <ArrowDown className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Product Info */}
                                        <div className="p-3">
                                            <h3 className="font-bold text-mekomit-900 text-base mb-1 line-clamp-1 flex items-center gap-2">
                                                {product.name}
                                                {soldOut && (
                                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-danger-100 text-danger-700 font-semibold">אזל</span>
                                                )}
                                            </h3>
                                            <p className="text-xs text-mekomit-700 mb-2 line-clamp-2">{product.description}</p>
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-xl font-bold text-mekomit-600">₪{product.price}</p>
                                                <p className={`text-[11px] font-medium ${soldOut ? 'text-danger-600' : 'text-mekomit-500'}`}>
                                                    {inv.enabled ? `מלאי: ${inv.quantity}` : 'מלאי: ∞'}
                                                </p>
                                            </div>

                                            {/* Extra options display */}
                                            {product.paid_options && product.paid_options.length > 0 && (
                                                <div className="mb-2 pb-2 border-t border-mekomit-100 pt-2">
                                                    <p className="text-[10px] font-semibold text-mekomit-700 mb-1">תוספות בתשלום:</p>
                                                    <div className="space-y-0.5">
                                                        {product.paid_options.map((option, idx) => (
                                                            <div key={idx} className="flex justify-between items-center text-[10px] text-mekomit-600">
                                                                <span className="line-clamp-1">{option.name}</span>
                                                                <span className="font-medium whitespace-nowrap mr-1">₪{option.price}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="flex gap-2 mb-2">
                                                <button
                                                    onClick={() => handleToggleAvailability(product)}
                                                    className={`flex-1 flex items-center justify-center gap-2 p-2 rounded-md transition-colors font-medium text-xs ${isAvailable
                                                        ? 'text-warning-600 bg-warning-50 hover:bg-warning-100'
                                                        : 'text-success-600 bg-success-50 hover:bg-success-100'
                                                        }`}
                                                    title={isAvailable ? 'הסתר מהחנות' : 'הצג בחנות'}
                                                >
                                                    {isAvailable ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                    <span>{isAvailable ? 'הסתר בחנות' : 'הצג בחנות'}</span>
                                                </button>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => { setEditingProduct(product); setShowModal(true); }}
                                                    className="flex-1 flex items-center justify-center gap-2 p-2 text-info-600 bg-info-50 hover:bg-info-100 rounded-md transition-colors font-medium text-xs"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                    <span>ערוך</span>
                                                </button>
                                                <button
                                                    onClick={() => handleDuplicate(product)}
                                                    disabled={duplicatingId === product.product_id}
                                                    className="flex-1 flex items-center justify-center gap-2 p-2 text-mekomit-600 bg-mekomit-50 hover:bg-mekomit-100 rounded-md transition-colors font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="צור עותק זהה של המוצר"
                                                >
                                                    <Copy className="w-4 h-4" />
                                                    <span>{duplicatingId === product.product_id ? 'משכפל...' : 'שכפל'}</span>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(product.product_id)}
                                                    className="flex-1 flex items-center justify-center gap-2 p-2 text-danger-600 bg-danger-50 hover:bg-danger-100 rounded-md transition-colors font-medium text-xs"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                    <span>מחק</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}
            </div>

            {showModal && <ProductModal storeId={storeId} store={store} product={editingProduct} categories={categories} onClose={() => { setShowModal(false); setEditingProduct(null); }} onSave={() => { setShowModal(false); setEditingProduct(null); refetch(); }} chatId={chatId} />}
        </div>
    );
};

export default ProductsTab;
